import Typography from '@mui/material/Typography';

import {
  useAccessToken,
  useSlug
} from 'reservations/account/providers/ConfigProvider';
import { BaseDrawer } from 'reservations/base/components/BaseDrawer';
import { useHasVisitId } from 'reservations/base/hooks/useQueryParams';
import { useLoadingSpinner } from 'reservations/base/providers/LoadingSpinnerProvider';
import { useModal } from 'reservations/base/providers/ModalProvider';
import { useCustomRouter } from 'reservations/base/providers/RouterProvider';
import {
  useBookingDetails,
  useBookingFee
} from 'reservations/booking/providers/BookingProvider';
import {
  HandleErrorOptions,
  HandleSuccessOptions
} from 'reservations/booking/types';
import { CONFIRM_CANCEL_RESERVATION } from 'reservations/cancel/constants';
import { useShouldAuthenticateForCancel } from 'reservations/cancel/hooks/useShouldAuthenticateForCancel';
import { useCancelBookingMutation } from 'reservations/cancel/queries/useCancelBookingMutation';
import { useCancelBookingMutationV2 } from 'reservations/cancel/queries/useCancelBookingMutationV2';
import { getLateCancelWarningText } from 'reservations/feePolicy/helpers';
import { useLateCancel } from 'reservations/feePolicy/hooks';

export const CancelReservationModal = () => {
  const { dateAndTime, partySize, bookingId } = useBookingDetails();
  const { handleIsLoading, handleHasLoaded } = useLoadingSpinner();
  const fee = useBookingFee();
  const { isLateCancel } = useLateCancel(dateAndTime, fee);
  const shouldAuthenticate = useShouldAuthenticateForCancel();
  const hasVisitId = useHasVisitId();
  const slug = useSlug();
  const accessToken = useAccessToken();
  const { close } = useModal();
  const { handleNavigation } = useCustomRouter();

  const handleCancelConfirmationModalClose = () => {
    handleHasLoaded();
    close();
  };

  // istanbul ignore next
  const handleSuccess = ({ navUrl }: HandleSuccessOptions) => {
    handleCancelConfirmationModalClose();
    if (navUrl) {
      handleNavigation(navUrl);
    }
  };

  // istanbul ignore next
  const handleError = ({ navUrl, forceReload = false }: HandleErrorOptions) => {
    close();

    if (navUrl || forceReload) {
      handleNavigation(navUrl, forceReload);
    }
  };

  const cancelMutationV1 = useCancelBookingMutation({
    slug,
    bookingId,
    apiKey: accessToken,
    handleSuccess,
    handleError
  });

  const cancelMutationV2 = useCancelBookingMutationV2();

  // istanbul ignore next
  const onSubmit = () => {
    handleIsLoading('Canceling your reservation...');
    hasVisitId ? cancelMutationV2.mutate() : cancelMutationV1.mutate();
  };

  return (
    <BaseDrawer
      header="CANCEL RESERVATION"
      subheader={CONFIRM_CANCEL_RESERVATION}
      primaryButton={{
        label: 'CANCEL RESERVATION',
        variant: 'error',
        onClick: onSubmit
      }}
      secondaryButton={{}}
      requireAuth={shouldAuthenticate}
      bookingDetailsDisplay="strikethrough"
    >
      {isLateCancel && (
        <Typography>{getLateCancelWarningText(fee!, partySize)}</Typography>
      )}
    </BaseDrawer>
  );
};
